import React, { PropsWithChildren } from 'react';

import { css, cx } from '@emotion/css';
import { Color, Font, Spacing } from 'src/styles';

export type MTypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle1'
  | 'subtitle2'
  | 'body1'
  | 'body2'
  | 'caption';

export interface MTypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant: MTypographyVariant;
  className?: string;
  color?: string;
  align?: 'left' | 'center' | 'right' | 'justify';
  weight?: 'light' | 'normal' | 'semibold' | 'bold';
  inline?: boolean;
  noWrap?: boolean;
  testId?: string;
}

const variantToElement: Record<MTypographyVariant, keyof JSX.IntrinsicElements> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'h6',
  subtitle2: 'h6',
  body1: 'p',
  body2: 'p',
  caption: 'span',
};

const baseStyle = (props: {
  color?: string;
  align?: string;
  noWrap?: boolean;
  weight?: 'light' | 'normal' | 'medium' | 'semibold' | 'bold';
  inline?: boolean;
}) =>
  css`
    ${Spacing.m0}
    ${Spacing.p0}
    color: ${props.color ?? 'inherit'};
    text-align: ${props.align ?? 'left'};
    white-space: ${props.noWrap ? 'nowrap' : undefined};
    overflow: ${props.noWrap ? 'hidden' : undefined};
    text-overflow: ${props.noWrap ? 'ellipsis' : undefined};
    font-weight: ${props.weight === 'light'
      ? 300
      : props.weight === 'normal'
      ? 400
      : props.weight === 'semibold'
      ? 600
      : props.weight === 'bold'
      ? 700
      : 400};
    line-height: 1.2;
    display: ${props.inline ? 'inline' : 'block'};
  `;

const subtitle2Style = css`
  ${Font.md}
  color: ${Color.Gray7};
`;

const variantStyles: Record<MTypographyVariant, string> = {
  h1: Font.h1,
  h2: Font.h2,
  h3: Font.h3,
  h4: Font.h4,
  h5: Font.h5,
  h6: Font.h6,
  subtitle1: Font.lg,
  subtitle2: subtitle2Style,
  body1: Font.md,
  body2: Font.sm,
  caption: Font.xs,
};

export const MTypography = ({
  variant,
  color,
  align,
  weight,
  noWrap,
  inline,
  className,
  children,
  testId,
}: PropsWithChildren<MTypographyProps>) => {
  const baseStyleClass = baseStyle({ color, align, weight, noWrap, inline });
  const variantStyleClass = variantStyles[variant];
  const combinedClassName = cx(baseStyleClass, variantStyleClass, className);
  const Element: React.ElementType = variantToElement[variant];

  return (
    <Element className={combinedClassName} data-testid={testId}>
      {children}
    </Element>
  );
};
